import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { api, firmAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { canViewClientInvoices } from '../utils/firmAccess';

const MonthlyCaseInvoiceSummary: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [clientFirmId, setClientFirmId] = useState<number | ''>('');
  const [clientFirms, setClientFirms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [totals, setTotals] = useState({ cases: 0, billed: 0, paid: 0, remaining: 0 });

  useEffect(() => {
    if (user && !canViewClientInvoices(user)) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadFirms = async () => {
      try {
        const res = await firmAPI.getAll({ type: 'client', per_page: 1000 });
        setClientFirms((res.data.data?.data || res.data.data || []).filter((f: any) => f.type === 'client'));
      } catch (err) {
        console.error('Failed to load firms:', err);
      }
    };
    loadFirms();
  }, []);

  const loadSummary = async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, any> = { year, month };
      if (clientFirmId) params.client_firm_id = clientFirmId;
      const res = await api.get('/client-invoices/monthly-summary', { params });
      if (res.data.success) {
        setRows(res.data.data.invoices || []);
        setTotals(res.data.data.totals || { cases: 0, billed: 0, paid: 0, remaining: 0 });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load monthly summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Monthly Case Invoice Summary</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        All case invoices billed in the selected month, with received amounts.
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Month</InputLabel>
            <Select label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem key={i + 1} value={i + 1}>{new Date(2000, i, 1).toLocaleString(undefined, { month: 'long' })}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[year - 2, year - 1, year, year + 1].map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Client firm</InputLabel>
            <Select label="Client firm" value={clientFirmId} onChange={(e) => setClientFirmId(e.target.value as number | '')}>
              <MenuItem value="">All</MenuItem>
              {clientFirms.map((firm) => (
                <MenuItem key={firm.id} value={firm.id}>{firm.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={loadSummary} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : 'Load'}
          </Button>
        </Box>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Typography>Cases: <strong>{totals.cases}</strong></Typography>
        <Typography>Billed: <strong>${Number(totals.billed).toFixed(2)}</strong></Typography>
        <Typography>Received: <strong>${Number(totals.paid).toFixed(2)}</strong></Typography>
        <Typography>Outstanding: <strong>${Number(totals.remaining).toFixed(2)}</strong></Typography>
      </Paper>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Invoice</TableCell>
              <TableCell>Case</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Billed</TableCell>
              <TableCell align="right">Received</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Link href="#" onClick={(e) => { e.preventDefault(); navigate(`/client-invoices/${row.id}`); }}>
                    {row.invoice_number}
                  </Link>
                </TableCell>
                <TableCell>#{row.case_id} {row.case_title}</TableCell>
                <TableCell>{row.client_firm}</TableCell>
                <TableCell>{row.invoice_date}</TableCell>
                <TableCell align="right">${Number(row.total_billing).toFixed(2)}</TableCell>
                <TableCell align="right">${Number(row.total_paid).toFixed(2)}</TableCell>
                <TableCell align="right">${Number(row.remaining).toFixed(2)}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.payment_status} color={row.payment_status === 'paid' ? 'success' : row.payment_status === 'partial' ? 'warning' : 'default'} />
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>No case invoices in this month.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MonthlyCaseInvoiceSummary;
